"use client";

import {
  format,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameDay,
  addHours,
  startOfDay,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Avatar, AvatarImage } from "@/components/ui/Avatar";
import type { CalendarEvent } from "./Calendar";
import UserSvg from "@/assets/userSvg.svg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { Badge } from "../Badge";
import { displayValue, getStatusColor } from "@/utils/Helper";
import { useState } from "react";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from "@/components/ui/Sheet";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import DetailLogs from "@/components/admin/tickets/ticket-detail/AuditLog";
interface WeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  dateRange: { start: Date; end: Date };
  onViewEvent?: (event: CalendarEvent) => void;
}

export function WeekView({
  currentDate,
  events,
  dateRange,
  onViewEvent,
}: WeekViewProps) {
    const [openLog, setOpenLog] = useState(false);
    const [ticketId, setTicketId] = useState<undefined | Number>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    day: Date | null;
    hour: number | null;
    events: CalendarEvent[];
  }>({
    day: null,
    hour: null,
    events: [],
  });
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const startDate = startOfWeek(currentDate, { weekStartsOn: 0 });
  const endDate = endOfWeek(startDate, { weekStartsOn: 0 });

  // const hours = Array.from({ length: 24 }, (_, i) => i + 6); // 6:00 to 21:00
  const hours = Array.from({ length: 24 }, (_, i) => i); // 0:00 to 23:00
  const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));

  // Get events for a specific day and hour
  const getEventsForTimeSlot = (day: Date, hour: number) => {
    const hourStart = addHours(startOfDay(day), hour);

    return events.filter((event) => {
      const eventStart = new Date(event.start);
      const eventHour = eventStart.getHours();
      return isSameDay(eventStart, day) && eventHour === hour;
    });
  };
   const handleViewEvent = (event: CalendarEvent) => {
     // Assuming your event has a ticket_id property
     setTicketId(Number(event.id));
     setOpenLog(true);
   };
  const handleViewMore = (
    day: Date,
    hour: number,
    eventsForSlot: CalendarEvent[]
  ) => {
    setSelectedTimeSlot({
      day,
      hour,
      events: eventsForSlot,
    });
    setIsSheetOpen(true);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 z-10 bg-white grid grid-cols-8 border-b">
        <div className="border-r py-2 text-center text-xs"></div>
        {days.map((day, index) => (
          <div
            key={day.toString()}
            className={cn(
              "py-2 text-center text-sm border-r"
              // index === 3 && "bg-green-50/30" // Highlight Wednesday like in the screenshot
            )}
          >
            <div>{format(day, "EEEE")}</div>
            <div className="text-xs">{format(day, "MMM d")}</div>
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto max-h-[65vh] fancy-scroll">
        <div className="grid grid-cols-8">
          <div className="border-r">
            {hours.map((hour) => (
              <div
                key={hour}
                className="h-36 border-b px-2 text-xs text-gray-500 relative"
              >
                <div className="absolute -top-2.5">{hour}:00</div>
              </div>
            ))}
          </div>
          {days.map((day, index) => (
            <div
              key={day.toString()}
              className={cn(
                "border-r",
                // index === 3 && "bg-green-50/30" // Highlight Wednesday like in the screenshot
              )}
            >
              {hours.map((hour) => {
                const eventsForSlot = getEventsForTimeSlot(day, hour);

                return (
                  <div key={hour} className="h-36 border-b p-1 flex flex-col">
                    <div className="flex-1 overflow-hidden">
                      {eventsForSlot.slice(0, 1).map((event) => (
                        <div
                          key={event.id}
                          className={cn(
                            "text-xs p-1 rounded-md border cursor-pointer transition-colors gap-2",
                            "bg-blue-50 border-blue-200 hover:bg-blue-100"
                          )}
                          onClick={() => handleViewEvent(event)}
                        >
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="font-medium truncate">
                                  {event.title}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-60 break-words">
                                {event.title}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {event.user?.name && (
                            <div>Name : {event.user?.name}</div>
                          )}
                          <div>
                            <Badge
                              className={`${getStatusColor(event.status) || "bg-gray-500"} text-white font-medium text-xs`}
                            >
                              {displayValue(event.status.replace(/_/g, " "))}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                    {eventsForSlot.length > 0 && (
                      <div className="mt-auto">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs text-blue-600 hover:text-blue-800 bg-white hover:bg-blue-50 h-6 truncate"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewMore(day, hour, eventsForSlot);
                          }}
                        >
                          View {eventsForSlot.length}{" "}
                          {eventsForSlot.length === 1 ? "ticke" : "tickets"}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Events Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {selectedTimeSlot.day && selectedTimeSlot.hour !== null
                ? `Events for ${format(selectedTimeSlot.day, "EEEE, MMMM d")} at ${selectedTimeSlot.hour}:00`
                : "Events"}
            </SheetTitle>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            {selectedTimeSlot.events.map((event) => (
              <Card
                key={event.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                // onClick={() => {
                //   onViewEvent?.(event);
                //   setIsSheetOpen(false);
                // }}
                onClick={() => handleViewEvent(event)}
              >
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">{event.title}</h3>
                  <div className="grid gap-1 text-sm">
                    <div className="font-medium">
                      Ticket Number: {event.ticket_number}
                    </div>
                    {event.user?.username && (
                      <div>ITS: {event.user?.username}</div>
                    )}
                    {event.user?.name && <div>Name: {event.user?.name}</div>}
                    <div className="mt-2">
                      <Badge
                        className={`${getStatusColor(event.status) || "bg-gray-500"} text-white font-medium text-xs`}
                      >
                        {displayValue(event.status.replace(/_/g, " "))}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </SheetContent>
      </Sheet>
      <DetailLogs isOpen={openLog} setOpen={setOpenLog} ticketId={ticketId} />
    </div>
  );
}
